import axiosInstance, { API_BASE_URL } from './axios-instance';

export interface KycDocument {
  id: string;
  document_type: string;
  file_name: string;
  original_file_name: string;
  file_size: number;
  mime_type: string;
  upload_status: 'pending' | 'uploaded' | 'verified' | 'rejected' | 'deleted';
  uploaded_at: string;
  verified_at?: string;
  rejected_at?: string;
  rejection_reason?: string;
  metadata?: any;
}

export interface UploadDocumentResponse {
  success: boolean;
  message: string;
  data: KycDocument;
}

export interface GetDocumentsResponse {
  success: boolean;
  data: KycDocument[];
  count: number;
}

export interface DocumentUploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

/**
 * Upload a KYC document
 */
export async function uploadKycDocument(
  file: File,
  documentType: string,
  onProgress?: (progress: DocumentUploadProgress) => void
): Promise<UploadDocumentResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('document_type', documentType);

  try {
    const response = await axiosInstance.post<UploadDocumentResponse>(
      `/api/kyc/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent: any) => {
          if (onProgress && progressEvent.total) {
            const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress({
              loaded: progressEvent.loaded,
              total: progressEvent.total,
              percentage,
            });
          }
        },
      }
    );

    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || error.response.data.error || 'Upload failed');
    }
    throw new Error(error.message || 'Upload failed');
  }
}

/**
 * Get all documents for current customer
 */
export async function getCustomerDocuments(
  documentType?: string,
  uploadStatus?: string
): Promise<GetDocumentsResponse> {
  const params = new URLSearchParams();
  if (documentType) params.append('document_type', documentType);
  if (uploadStatus) params.append('upload_status', uploadStatus);

  try {
    const response = await axiosInstance.get<GetDocumentsResponse>(
      `/api/kyc/documents?${params.toString()}`
    );

    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || error.response.data.error || 'Failed to fetch documents');
    }
    throw new Error(error.message || 'Failed to fetch documents');
  }
}

/**
 * Get single document details
 */
export async function getDocument(documentId: string): Promise<{ success: boolean; data: KycDocument }> {
  try {
    const response = await axiosInstance.get(
      `/api/kyc/documents/${documentId}`
    );

    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || error.response.data.error || 'Failed to fetch document');
    }
    throw new Error(error.message || 'Failed to fetch document');
  }
}

/**
 * Get document download URL
 */
export function getDocumentDownloadUrl(documentId: string): string {
  const token = localStorage.getItem('authToken');
  
  if (!token) {
    throw new Error('Authentication required');
  }
  
  return `${API_BASE_URL}/api/kyc/documents/${documentId}/download?token=${token}`;
}

/**
 * Download/view document
 */
export async function downloadDocument(documentId: string): Promise<void> {
  try {
    const url = getDocumentDownloadUrl(documentId);
    window.open(url, '_blank');
  } catch (error: any) {
    throw new Error(error.message || 'Failed to download document');
  }
}

/**
 * Delete document
 */
export async function deleteDocument(documentId: string): Promise<{ success: boolean; message: string }> {
  try {
    const response = await axiosInstance.delete(
      `/api/kyc/documents/${documentId}`
    );

    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || error.response.data.error || 'Failed to delete document');
    }
    throw new Error(error.message || 'Failed to delete document');
  }
}

/**
 * Validate file before upload
 */
export function validateFile(file: File): { valid: boolean; error?: string } {
  const maxSize = 10 * 1024 * 1024; // 10MB
  const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];

  if (file.size > maxSize) {
    return {
      valid: false,
      error: 'ไฟล์มีขนาดใหญ่เกินไป (สูงสุด 10MB)',
    };
  }

  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: 'ประเภทไฟล์ไม่ถูกต้อง (รองรับเฉพาะ JPEG, PNG, PDF)',
    };
  }

  return { valid: true };
}

/**
 * Get document type label in Thai
 */
export function getDocumentTypeLabel(documentType: string): string {
  const labels: Record<string, string> = {
    id_card_front: 'บัตรประชาชนด้านหน้า',
    id_card_back: 'บัตรประชาชนด้านหลัง',
    house_registration: 'ทะเบียนบ้าน',
    company_registration: 'หนังสือจดทะเบียนบริษัท',
    director_id_card: 'บัตรประชาชนกรรมการ',
    additional_document: 'เอกสารเพิ่มเติม',
  };

  return labels[documentType] || documentType;
}

/**
 * Get upload status label in Thai
 */
export function getUploadStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: 'รอดำเนินการ',
    uploaded: 'อัพโหลดแล้ว',
    verified: 'ตรวจสอบแล้ว',
    rejected: 'ถูกปฏิเสธ',
    deleted: 'ถูกลบ',
  };

  return labels[status] || status;
}

/**
 * Get upload status color
 */
export function getUploadStatusColor(status: string): string {
  const colors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    uploaded: 'bg-blue-100 text-blue-800',
    verified: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    deleted: 'bg-gray-100 text-gray-800',
  };

  return colors[status] || 'bg-gray-100 text-gray-800';
}

/**
 * Format file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}
